'use client'
import classNames from 'classnames'
import useRoomPlanSelect from 'app/hooks/useRoomPlanSelect'
import useQueryHotelDetail from 'app/hooks/useQueryHotelDetail'
import { useState } from 'react'
import { useTranslation } from 'app/i18n/client'

import { Button, Modal, ModalHeader } from 'app/components/atoms'
import HotelPlanCard from '../HotelPlanCard/Component'

import CrossIcon from 'public/icons/cross.svg'

import PlanListModalProps from './PlanListModal.props'
import useAuthentication from 'app/hooks/useAuthentication'
import { Trans } from 'react-i18next'

const PlanListModal: React.FunctionComponent<PlanListModalProps> = ({
  isMultipleSelect,
  totalSearchRoom,
  canEdit,
  canSelect = canEdit,
  canEditRoomNumber = false,
  open,
  onClose,
  items,
  onViewPayment,
  onViewDetail,
  onConfirmPayment,
}) => {
  const { t } = useTranslation()

  const [submitReservationType, setSubmitReservationType] = useState<
    'regular' | 'membership'
  >()

  const { isAuthenticated } = useAuthentication()

  const {
    roomPlanList,
    selectedRoomPlanList,
    isViewingDetailMultiple,
    onEdit,
    onSelect,
    onViewDetailSingle,
    onViewDetailMultiple,
    onReserveSingle,
    onReserveMultiple,
    onUpdatePrice,
  } = useRoomPlanSelect(
    items,
    totalSearchRoom,
    canEdit || canEditRoomNumber,
    onViewDetail,
    onViewPayment,
    items,
  )

  const { data: hotelDetail } = useQueryHotelDetail(items?.[0]?.hotelCode)

  const onReserve = (type: 'regular' | 'membership') => () => {
    setSubmitReservationType(type)

    if (canSelect) {
      onReserveMultiple(items[0]?.hotelCode, type)
      return
    }

    onConfirmPayment && onConfirmPayment(type)
  }

  const renderTitle = () => {
    return (
      <Trans i18nKey="Available Plans for">
        Available Plans for{' '}
        <div className="block md:inline-block text-primary">
          {{ hotelName: hotelDetail?.name } as any}
        </div>
      </Trans>
    )
  }

  return (
    <Modal
      position="bottom"
      open={open}
      onClose={onClose}
      containerClassName="!pt-0 !px-0 !pb-0 !w-full !h-full lg:items-center"
    >
      <div className="flex flex-col bg-gray-bg shadow-xl transform transition-all lg:align-middle w-full h-3/4 lg:h-auto lg:w-256 max-h-full lg:max-h-3/5 rounded-t-2.5xl lg:rounded-t-none">
        <div className="hidden lg:block py-6 px-10">
          <ModalHeader
            onClose={onClose}
            label={<label>{renderTitle()}</label>}
          />
        </div>
        <div className="hidden lg:block border-t border-gray-50 mx-5"></div>
        <div className="relative lg:hidden pt-6 pb-4 text-center">
          <label className="font-lora text-lg">{renderTitle()}</label>
          <div
            onClick={onClose}
            className="absolute top-4 right-4 w-8 h-8 bg-transparent flex items-center justify-center"
          >
            <CrossIcon className="fill-current text-secondary" />
          </div>
        </div>
        <div className="py-0 lg:py-8 px-5 lg:px-10 pb-10 px-2 overflow-y-auto">
          <div
            className={classNames(
              'mb-16 lg:mb-8 space-y-4 lg:space-y-0 space-x-0 lg:space-x-5',
              {
                hidden: !isMultipleSelect,
                'static lg:flex': isMultipleSelect,
              },
            )}
          >
            <div className="flex space-x-5 lg:space-x-4 justify-end w-full">
              <div className="lg:w-56 h-12 lg:h-auto flex-1 lg:flex-initial">
                <Button
                  className="uppercase w-full h-full"
                  size="sm"
                  disabled={
                    (selectedRoomPlanList?.length === 0 && canSelect) ||
                    isAuthenticated
                  }
                  onClick={onReserve('regular')}
                  loading={submitReservationType === 'regular'}
                >
                  <Trans i18nKey="Book with regular price">
                    Book with <br className="lg:hidden" /> regular price
                  </Trans>
                </Button>
              </div>
              <div className="lg:w-56 h-12 lg:h-auto flex-1 lg:flex-initial">
                <Button
                  className="uppercase w-full h-full"
                  size="sm"
                  disabled={selectedRoomPlanList?.length === 0 && canSelect}
                  onClick={onReserve('membership')}
                  loading={submitReservationType === 'membership'}
                >
                  <Trans i18nKey="Book with membership price">
                    Book with <br className="lg:hidden" /> membership price
                  </Trans>
                </Button>
              </div>
            </div>
            {canSelect && (
              <div className="w-full lg:w-40">
                <Button
                  className={classNames(
                    'w-full flex-1 lg:flex-initial !py-2.5 !px-8 uppercase',
                    {
                      '!bg-white !text-gray-dark border border-gray-150':
                        selectedRoomPlanList?.length !== 0,
                      '!bg-gray-100': selectedRoomPlanList?.length === 0,
                    },
                  )}
                  size="sm"
                  disabled={selectedRoomPlanList?.length === 0}
                  onClick={() => onViewDetailMultiple(items[0]?.hotelCode)}
                  loading={isViewingDetailMultiple}
                  loadingSpinerClassName="!text-gray-dark"
                  loadingClassName="!text-white"
                >
                  {t('Details')}
                </Button>
              </div>
            )}
          </div>
          <div className="space-y-6 -mt-10 lg:mt-0">
            {roomPlanList?.length === 0 ? (
              <div className="py-8 w-full text-center text-lg font-lora text-gray-50">
                {t('No result found')}
              </div>
            ) : (
              roomPlanList?.map((item, index) => (
                <HotelPlanCard
                  {...item}
                  key={`search-item-${index}`}
                  type="roomPlan"
                  isMultipleSelect={isMultipleSelect}
                  canEdit={canEdit}
                  canSelect={canSelect}
                  canEditRoomNumber={canEditRoomNumber}
                  onEdit={onEdit(item.id)}
                  onSelect={onSelect(item.id)}
                  onViewDetail={onViewDetailSingle(item?.hotelCode, {
                    room_plan_code: item.roomPlanCode,
                    number_of_adult: item.adultNumber,
                    number_of_children: item.childrenNumber,
                    number_of_room: item.roomNumber,
                    membership_promotion_id: item.membershipPromotionId,
                    hotel_plan_code: item.hotelPlanCode,
                  })}
                  onReserve={onReserveSingle(item?.hotelCode, {
                    room_plan_code: item.roomPlanCode,
                    number_of_adult: item.adultNumber,
                    number_of_children: item.childrenNumber,
                    number_of_room: item.roomNumber,
                    membership_promotion_id: item.membershipPromotionId,
                    hotel_plan_code: item.hotelPlanCode,
                  })}
                  onUpdatePrice={onUpdatePrice(item?.id)}
                />
              ))
            )}
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default PlanListModal
